// Плавное постраничное перелистывание главной.
// Браузерный scroll-snap доводит до экрана резко и с непредсказуемой
// скоростью, поэтому на ПК листаем сами: один жест колеса / клавиша —
// один экран, мягкая анимация с разгоном и торможением. Экран выше окна
// (длинный FAQ) сначала докручивается внутри себя.
// На тач-устройствах остаётся нативная прокрутка со scroll-snap.

const DURATION_MS = 1000;
/** После анимации ещё столько ждём, чтобы инерция тачпада не листнула дальше. */
const COOLDOWN_MS = 450;
/** Порог накопленного колеса для одного шага (тачпад шлёт мелкие события). */
const WHEEL_THRESHOLD = 40;

/** Мягкий разгон и торможение. */
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

let container: HTMLElement | null = null;
let animating = false;
let lockedUntil = 0;
let frame = 0;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Плавно прокрутить контейнер главной до позиции. */
export function animateScrollTo(target: number, duration = DURATION_MS): void {
  const el = container;
  if (!el) return;
  const max = el.scrollHeight - el.clientHeight;
  const to = Math.max(0, Math.min(max, Math.round(target)));
  const from = el.scrollTop;
  if (Math.abs(to - from) < 2) return;
  cancelAnimationFrame(frame);
  if (reducedMotion()) {
    el.scrollTop = to;
    return;
  }
  animating = true;
  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / duration);
    el.scrollTop = from + (to - from) * ease(t);
    if (t < 1) {
      frame = requestAnimationFrame(step);
    } else {
      animating = false;
      lockedUntil = performance.now() + COOLDOWN_MS;
    }
  };
  frame = requestAnimationFrame(step);
}

/** Экраны главной по порядку (как у точек справа). */
const SECTION_IDS = ['main', 'advantages', 'slidercontainer', 'howtoinstall', 'latestnews', 'faq', 'community'];

/** Точки остановки: начала экранов главной и самый низ (футер). */
function stops(el: HTMLElement): number[] {
  const max = el.scrollHeight - el.clientHeight;
  const tops = SECTION_IDS
    .map((id) => document.getElementById(id))
    .filter((s): s is HTMLElement => !!s)
    .map((s) => s.offsetTop)
    .filter((t) => t <= max);
  tops.push(max);
  return Array.from(new Set(tops.map((t) => Math.round(t)))).sort((a, b) => a - b);
}

/** Плавно перейти к секции по id (точки справа, кнопки «Начать играть»). */
export function scrollToSection(id: string): void {
  const target = document.getElementById(id);
  if (!target) return;
  if (!container) {
    target.scrollIntoView({ behavior: 'smooth' });
    return;
  }
  animateScrollTo(target.offsetTop);
}

function go(direction: 1 | -1): void {
  const el = container;
  if (!el) return;
  const pos = el.scrollTop;
  const view = el.clientHeight;
  const points = stops(el);
  // Текущий экран — последняя точка не ниже текущей позиции
  let i = 0;
  for (let k = 0; k < points.length; k++) if (points[k] <= pos + 2) i = k;
  const curTop = points[i];
  const nextTop = points[i + 1];

  if (direction > 0) {
    // Экран выше окна: сначала докручиваем его до конца
    // (если лишнего меньше пятой части окна — это просто отступы, листаем дальше)
    if (nextTop !== undefined && nextTop - (pos + view) > view * 0.2) {
      animateScrollTo(Math.min(pos + view * 0.85, nextTop - view), 750);
      return;
    }
    if (nextTop !== undefined) animateScrollTo(nextTop);
    return;
  }
  // Вверх: если мы внутри длинного экрана — сначала к его началу
  if (pos - curTop > 4) {
    animateScrollTo(Math.max(curTop, pos - view * 0.85), pos - curTop > view ? 750 : DURATION_MS);
    return;
  }
  const prevTop = points[i - 1];
  if (prevTop !== undefined) {
    // Предыдущий экран выше окна — встаём на его низ
    const prevBottom = curTop - view;
    animateScrollTo(prevBottom - prevTop > view * 0.2 ? prevBottom : prevTop);
  }
}

/**
 * Включить плавное перелистывание на контейнере главной.
 * Возвращает функцию отключения.
 */
export function enablePagedScroll(el: HTMLElement): () => void {
  container = el;
  let wheelAcc = 0;
  let wheelTimer = 0;

  const onWheel = (e: WheelEvent) => {
    if (e.ctrlKey) return; // зум страницы
    // Прокрутка внутри своих скролл-областей (списки, окна) — не трогаем
    const inner = (e.target as HTMLElement).closest('[data-own-scroll]');
    if (inner) return;
    e.preventDefault();
    if (animating || performance.now() < lockedUntil) {
      // Инерция тачпада продлевает блокировку, пока жест не закончится
      if (!animating) lockedUntil = performance.now() + 120;
      wheelAcc = 0;
      return;
    }
    wheelAcc += e.deltaY;
    window.clearTimeout(wheelTimer);
    wheelTimer = window.setTimeout(() => { wheelAcc = 0; }, 200);
    if (Math.abs(wheelAcc) >= WHEEL_THRESHOLD) {
      go(wheelAcc > 0 ? 1 : -1);
      wheelAcc = 0;
    }
  };

  const onKey = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    // Пробел на кнопке/ссылке — её нажатие, а не прокрутка
    if (t && (t.tagName === 'BUTTON' || t.tagName === 'A') && e.key === ' ') return;
    if (document.body.classList.contains('dl-open') || document.querySelector('[role=dialog]')) return;
    const down = ['ArrowDown', 'PageDown', ' '].includes(e.key) && !(e.key === ' ' && e.shiftKey);
    const up = ['ArrowUp', 'PageUp'].includes(e.key) || (e.key === ' ' && e.shiftKey);
    if (!down && !up && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    if (animating) return;
    if (e.key === 'Home') animateScrollTo(0);
    else if (e.key === 'End') animateScrollTo(el.scrollHeight);
    else go(down ? 1 : -1);
  };

  el.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKey);
  return () => {
    el.removeEventListener('wheel', onWheel);
    window.removeEventListener('keydown', onKey);
    cancelAnimationFrame(frame);
    animating = false;
    if (container === el) container = null;
  };
}

/** Плавное перелистывание только для мыши/тачпада, не для тач-экранов. */
export const canUsePagedScroll = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
